const anotherSole_cartNudge = () => {

  // ─── Guard: only one script instance runs ────────────────────────────────────
  if (window._as_cartNudgeInit) return;
  window._as_cartNudgeInit = true;

  const overlay            = document.getElementById('as-overlay');
  const modal              = document.getElementById('as-modal');
  const closeBtn           = document.getElementById('as-close');
  const dismissBg          = document.getElementById('as-nudge-dismiss');
  const itemsContainer     = document.getElementById('as-items');
  const itemTemplate       = document.getElementById('as-item-template');
  const moreWrap           = document.getElementById('as-more-wrap');
  const moreBtn            = document.getElementById('as-more-btn');
  const itemCountEl        = document.getElementById('as-item-count');
  const itemPluralEl       = document.getElementById('as-item-plural-s');
  const subtotalEl         = document.getElementById('as-subtotal-value');
  // Discount elements are optional — older cached markup may not have them yet,
  // so they're not part of the required-elements guard below.
  const discountsContainer = document.getElementById('as-discounts');
  const discountTemplate   = document.getElementById('as-discount-template');

  if (!overlay || !modal || !closeBtn || !dismissBg || !itemsContainer || !itemTemplate) {
    console.error('[AnotherSole] Cart nudge markup not found — did you render {% render "cart-nudge" %} on this page?');
    return;
  }

  const INITIAL_COUNT = 2;

  // ─── Session dismissal ───────────────────────────────────────────────────
  const SESSION_KEY = 'as_nudge_dismissed_session';
  const isSessionDismissed = () => {
    try { return sessionStorage.getItem(SESSION_KEY) === 'true'; } catch (e) { return false; }
  };
  const markSessionDismissed = () => {
    try { sessionStorage.setItem(SESSION_KEY, 'true'); } catch (e) {}
  };

  // ─── 30-day lifecycle cap ────────────────────────────────────────────────
  const LIFECYCLE_KEY = 'as_nudge_first_shown';
  const LIFECYCLE_MS = 30 * 24 * 60 * 60 * 1000; // 30 days

  const isLifecycleExpired = () => {
    try {
      const raw = localStorage.getItem(LIFECYCLE_KEY);
      if (!raw) return false;
      const firstShownAt = parseInt(raw, 10);
      if (!firstShownAt) return false;
      return (Date.now() - firstShownAt) > LIFECYCLE_MS;
    } catch (e) {
      return false;
    }
  };

  const markFirstShown = () => {
    try {
      if (!localStorage.getItem(LIFECYCLE_KEY)) {
        localStorage.setItem(LIFECYCLE_KEY, String(Date.now()));
      }
    } catch (e) {}
  };

  const canShowNudge = () => !isSessionDismissed() && !isLifecycleExpired();

  // ─── Recurring show cycle ──────────────────────────────────────────────────
  const NUDGE_DELAY_MS = 20000;
  let nudgeTimer = null;

  // Generic scheduler — takes whatever "show" function should run once the
  // delay elapses, so the initial (DOM-only) show and the post-add-to-cart
  // (fetch-based) show can share the same timer plumbing.
  const scheduleShow = (showFn, delay = NUDGE_DELAY_MS) => {
    if (!canShowNudge()) return;
    if (nudgeTimer) clearTimeout(nudgeTimer);
    nudgeTimer = setTimeout(showFn, delay);
  };

  // ─── Helpers ─────────────────────────────────────────────────────────────────
  // Theme doesn't display a currency code, and trims a trailing ".00" the
  // same way the theme's `money_without_trailing_zeros` Liquid filter does
  // (used for the server-rendered items/subtotal/discounts in the snippet).
  // Update this if the theme's money_format setting ever changes.
  const formatMoney = (cents) => {
    const amount = cents / 100;
    const isWhole = Math.round(amount * 100) % 100 === 0;
    const formatted = amount.toLocaleString('en-US', {
      minimumFractionDigits: isWhole ? 0 : 2,
      maximumFractionDigits: isWhole ? 0 : 2,
    });
    return `$${formatted}`;
  };

  // "Size: US5.5" — mirrors the Liquid markup in cart-nudge.liquid so the
  // server-rendered and JS-rebuilt rows read identically. Returns '' for
  // products with only a default variant, which have nothing worth showing.
  const formatVariantOptions = (item) => {
    if (item.product_has_only_default_variant) return '';

    const options = item.options_with_values || [];
    if (options.length === 0) return '';

    return options
      .filter((option) => option && option.value)
      .map((option) => `${option.name}: ${option.value}`)
      .join(', ');
  };

  // Build a single item's DOM node by cloning the <template> — no innerHTML strings
  const buildItemNode = (item, index) => {
    const node      = itemTemplate.content.cloneNode(true);
    const wrapper   = node.querySelector('.as-item');
    const imgLink   = node.querySelector('.as-item-img-link');
    const img       = node.querySelector('.as-item-img');
    const titleLink = node.querySelector('.as-item-title-link');
    const variantEl = node.querySelector('.as-item-variant');
    const priceEl   = node.querySelector('.as-item-price');

    const imgSrc = item.image ? item.image.replace(/(\.[^.]+)$/, '_200x200$1') : '';
    const productUrl = item.url || `/products/${item.handle}`;

    if (imgSrc) {
      img.src = imgSrc;
      img.alt = item.product_title;
      img.addEventListener('error', () => imgLink.classList.add('as-img-error'));
    } else {
      img.remove();
    }

    imgLink.href = productUrl;
    titleLink.href = productUrl;
    titleLink.textContent = item.product_title;

    // Guarded because older cached markup may not carry the variant element.
    if (variantEl) {
      const variantText = formatVariantOptions(item);
      variantEl.textContent = variantText;
      variantEl.hidden = variantText === '';
    }

    priceEl.textContent = formatMoney(item.final_price);

    // Mark items beyond the initially-visible count, using the same
    // convention as the Liquid markup, so one "show more" handler works
    // for both the server-rendered and JS-rebuilt states.
    if (index >= INITIAL_COUNT) {
      wrapper.classList.add('as-item-extra');
      wrapper.hidden = true;
    }

    return node;
  };

  // Build a single cart-level discount row by cloning the <template>,
  // mirroring the Liquid markup so both render paths look identical.
  const buildDiscountNode = (discount) => {
    const node     = discountTemplate.content.cloneNode(true);
    const titleEl  = node.querySelector('.as-discount-title');
    const valueEl  = node.querySelector('.as-discount-value');

    titleEl.textContent = discount.title;
    valueEl.textContent = `-${formatMoney(discount.total_allocated_amount)}`;

    return node;
  };

  // Expand cart.items (one entry per *line*, i.e. per unique variant) into
  // one entry per *unit* — so a line with quantity 2 produces two rows in
  // the popup, matching cart.item_count instead of cart.items.length.
  const expandItemsByQuantity = (items) => {
    const expanded = [];
    (items || []).forEach((item) => {
      const qty = item.quantity || 1;
      for (let i = 0; i < qty; i++) {
        expanded.push(item);
      }
    });
    return expanded;
  };

  // ─── Close Popup ─────────────────────────────────────────────────────────────
  const closePopup = () => {
    if (!overlay.classList.contains('as-shown')) return;
    overlay.classList.remove('as-visible');
    overlay.setAttribute('aria-hidden', 'true');
    setTimeout(() => overlay.classList.remove('as-shown'), 300);
    markSessionDismissed();
    if (nudgeTimer) clearTimeout(nudgeTimer);
  };

  closeBtn.addEventListener('click', closePopup);
  dismissBg.addEventListener('click', closePopup);

  // ─── "Show more" toggle ──────────────────────────────────────────────────
  // Works against whatever is currently in #as-items — either the
  // Liquid-rendered initial items or a JS-rebuilt set — as long as extra
  // items carry the .as-item-extra class + hidden attribute.
  moreBtn.addEventListener('click', () => {
    const expanded = moreBtn.dataset.expanded === 'true';
    itemsContainer.querySelectorAll('.as-item-extra').forEach(el => { el.hidden = expanded; });
    moreBtn.textContent = expanded ? moreBtn.dataset.moreLabel : 'Show less';
    moreBtn.dataset.expanded = expanded ? 'false' : 'true';
  });

  // Sets the button's label/visibility for a given extra-item count and
  // resets it to the collapsed state. Used whenever items are rebuilt.
  const setupMoreButton = (extraCount) => {
    if (extraCount > 0) {
      const label = `+ ${extraCount} more item${extraCount !== 1 ? 's' : ''}`;
      moreWrap.hidden = false;
      moreBtn.textContent = label;
      moreBtn.dataset.moreLabel = label;
      moreBtn.dataset.expanded = 'false';
    } else {
      moreWrap.hidden = true;
      delete moreBtn.dataset.moreLabel;
    }
  };

  // ─── Show popup (assumes #as-items is already populated) ────────────────
  const showPopup = () => {
    overlay.setAttribute('aria-hidden', 'false');
    overlay.classList.add('as-shown');
    requestAnimationFrame(() => requestAnimationFrame(() => {
      overlay.classList.add('as-visible');
    }));
    markFirstShown();
  };

  // ─── Populate popup from a freshly-fetched cart object ───────────────────
  // NOTE: `items` here is expected to already be expanded-by-quantity
  // (one entry per unit) by the caller — see buildPopup below.
  const renderItems = (items) => {
    itemsContainer.innerHTML = '';
    const frag = document.createDocumentFragment();
    items.forEach((item, index) => frag.appendChild(buildItemNode(item, index)));
    itemsContainer.appendChild(frag);
  };

  // Renders cart-level discounts (e.g. "2GET50 -$50.00"). Hides the
  // container entirely when there's nothing applied.
  const renderDiscounts = (discounts) => {
    if (!discountsContainer || !discountTemplate) return;

    discountsContainer.innerHTML = '';

    if (!discounts || discounts.length === 0) {
      discountsContainer.hidden = true;
      return;
    }

    const frag = document.createDocumentFragment();
    discounts.forEach((discount) => frag.appendChild(buildDiscountNode(discount)));
    discountsContainer.appendChild(frag);
    discountsContainer.hidden = false;
  };

  const buildPopup = (cart) => {
    if (!cart || cart.item_count < 1) return;
    if (!canShowNudge()) return;
    if (overlay.classList.contains('as-shown')) return;

    // Expand line items by quantity so a qty-2 line shows as 2 separate
    // rows — count now matches cart.item_count, not cart.items.length.
    const items = expandItemsByQuantity(cart.items);

    itemCountEl.textContent = cart.item_count;
    itemPluralEl.textContent = cart.item_count !== 1 ? 's' : '';
    subtotalEl.textContent = formatMoney(cart.total_price);

    renderItems(items);
    setupMoreButton(Math.max(0, items.length - INITIAL_COUNT));
    renderDiscounts(cart.cart_level_discount_applications || []);

    showPopup();
  };

  // ─── Initial show: use the cart already rendered by Liquid, no fetch ────
  const showInitialPopupIfEligible = () => {
    if (!canShowNudge()) return;
    if (overlay.classList.contains('as-shown')) return;
    if (itemsContainer.children.length === 0) return; // nothing server-rendered — cart was empty on load
    showPopup();
  };

  // ─── Fetch cart & show (used after an add-to-cart, when the server-rendered
  //     items are stale) ────────────────────────────────────────────────────
  const fetchCartAndShow = async () => {
    if (!canShowNudge()) return;
    if (overlay.classList.contains('as-shown')) return;

    try {
      const response = await fetch('/cart.js', { credentials: 'same-origin' });
      const cart = await response.json();
      if (cart && cart.item_count >= 1) {
        buildPopup(cart);
      }
    } catch (err) {
      console.error('[AnotherSole] Error fetching cart:', err);
    }
  };

  // ─── 30-day lifecycle check ──────────────────────────────────────────────
  if (isLifecycleExpired()) {
    return;
  }

  // ─── Wire up "show more" for whatever Liquid already rendered ───────────
  if (!moreWrap.hidden) {
    moreBtn.dataset.moreLabel = moreBtn.textContent.trim();
    moreBtn.dataset.expanded = 'false';
  }

  // Attach the same error handling to any images Liquid already rendered
  itemsContainer.querySelectorAll('.as-item-img').forEach((img) => {
    img.addEventListener('error', () => {
      const link = img.closest('.as-item-img-link');
      if (link) link.classList.add('as-img-error');
    });
  });

  // ─── Trigger: first show 20s after page load (if cart already has items) ────
  scheduleShow(showInitialPopupIfEligible, NUDGE_DELAY_MS);

  // ─── Trigger: item added to cart → re-fetch and show again 20s later ────────
  function onItemAdded() {
    scheduleShow(fetchCartAndShow, NUDGE_DELAY_MS);
  }

  // ─── Hook Shopify cart add (fetch + XHR) ─────────────────────────────────────
  if (!window._as_cartHook) {
    window._as_cartHook = true;

    const origFetch = window.fetch;
    window.fetch = function(...args) {
      const url = typeof args[0] === 'string' ? args[0] : '';
      const isCartAdd = url.includes('/cart/add');

      return origFetch.apply(this, args).then(response => {
        if (isCartAdd && response.ok) {
          setTimeout(() => {
            window.dispatchEvent(new CustomEvent('as-item-added'));
          }, 500);
        }
        return response;
      });
    };

    const origOpen = XMLHttpRequest.prototype.open;
    const origSend = XMLHttpRequest.prototype.send;

    XMLHttpRequest.prototype.open = function(method, url, ...rest) {
      this._as_url = url;
      return origOpen.apply(this, [method, url, ...rest]);
    };

    XMLHttpRequest.prototype.send = function(...args) {
      if (this._as_url && this._as_url.includes('/cart/add')) {
        this.addEventListener('load', () => {
          if (this.status >= 200 && this.status < 300) {
            setTimeout(() => {
              window.dispatchEvent(new CustomEvent('as-item-added'));
            }, 500);
          }
        });
      }
      return origSend.apply(this, args);
    };
  }

  // ─── Listen for item added events ─────────────────────────────────────────────
  window.addEventListener('as-item-added', onItemAdded);
};

if (document.readyState !== 'loading') {
  anotherSole_cartNudge();
} else {
  document.addEventListener('DOMContentLoaded', anotherSole_cartNudge);
}